import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldOff, UserX, Unlock, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import EmptyState from "../components/EmptyState";

import { API } from "../config";
import { tx } from "../lib/tx";

export default function BlockedUsers() {
  const navigate = useNavigate();
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/blocked-users`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBlockedUsers(data.blocked_users || []);
      }
    } catch (error) {
      console.error("Error fetching blocked users:", error);
    } finally {
      setLoading(false);
    }
  };

  const unblockUser = async (userId) => {
    setUnblocking(userId);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/blocked-users/${userId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        setBlockedUsers(prev => prev.filter(u => u.user_id !== userId));
      }
    } catch (error) {
      console.error("Error unblocking user:", error);
    } finally {
      setUnblocking(null);
    }
  };

  const getPhotoUrl = (user) => {
    if (user.main_photo) {
      if (user.main_photo.startsWith('http')) return user.main_photo;
      return `${API}${user.main_photo.replace('/api', '')}`;
    }
    return user.photos?.[0] || '/default-avatar.png';
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="flex items-center gap-2">
            <ShieldOff className="w-6 h-6 text-red-500" />
            <h1 className="font-heading text-xl font-bold text-white">{tx("Membres bloqués")}</h1>
          </div>
          {blockedUsers.length > 0 && (
            <span className="ml-auto bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-semibold">
              {blockedUsers.length}
            </span>
          )}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Info banner */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200/80">
            {tx("Les membres bloqués ne peuvent plus vous voir, vous contacter ou voir votre profil.")}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : blockedUsers.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <UserX className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl text-white mb-2">{tx("Aucun membre bloqué")}</h3>
            <p className="text-muted-foreground">{tx("Vous n'avez bloqué personne pour le moment")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {blockedUsers.map((user, index) => (
              <motion.div
                key={user.user_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-card/50 border border-white/10 rounded-2xl p-4 flex items-center gap-4"
              >
                {/* Photo */}
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 grayscale opacity-60">
                  <img
                    src={getPhotoUrl(user)}
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate">
                    {user.name}, {user.age}
                  </h3>
                  <p className="text-sm text-red-400 flex items-center gap-1">
                    <ShieldOff className="w-3 h-3" />
                    {tx("Bloqué")}
                  </p>
                  {user.blocked_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {tx("Le")} {new Date(user.blocked_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>

                {/* Unblock button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => unblockUser(user.user_id)}
                  disabled={unblocking === user.user_id}
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                >
                  {unblocking === user.user_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Unlock className="w-4 h-4 mr-1" />
                      {tx("Débloquer")}
                    </>
                  )}
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
